import { useLoadingState } from '@common/hooks';
import { filterNotAvailableEntities } from '@features/EntitiesListing/utils';
import { TranslationsFunc, useTranslations } from '@lang';
import { useQueryClient } from '@tanstack/react-query';
import { mapDiscountToDisplayObject } from '@utils/mapDiscountToDisplayObject';
import { useEffect, useRef } from 'react';

import { useGetAllSpaPackages } from '@features/spa/hooks/useGetAllSpaPackages';
import {
  getAvailableSpaPackagesKey,
  useGetAvailableSpaPackages,
} from '@features/spa/hooks/useGetAvailableSpaPackages';
import { useGetSpaDaysPage } from '@features/spa/hooks/useGetSpaDaysPage';

import { CategoryFilterValue } from '@features/spa/hooks/useSpaEntityFilter';
import { UNCATEGORIZED_VALUE } from '@features/spa/utils/buildCategoryOptions';
import { SpaPackageListingType } from 'shared-types/SpaPackage';
import { SpaListItem } from 'voyager';

type UseSpaDaysListParams = {
  propertyId: number;
  language: string;
  categoryFilterId: number | null;
  categoryFilterValue: CategoryFilterValue;
  date: string;
  guests?: number;
};

const mapToListItem = (
  sd: SpaPackageListingType,
  t: TranslationsFunc,
  fallbackImageUrl?: string
): SpaListItem => ({
  id: String(sd.id),
  name: sd.name,
  imageUrl: sd.image?.url || fallbackImageUrl,
  price:
    sd.price?.amount != null
      ? { amount: sd.price.amount, currencyCode: sd.price.currencyCode }
      : undefined,
  discount: mapDiscountToDisplayObject(sd.discount),
  promotionName: sd.discount?.name,
  duration: sd.durationText ? t(sd.durationText) : undefined,
  durationValue: sd.durationText ?? undefined,
  minGuests: sd.minimumGuests ?? sd.numberOfGuests,
  maxGuests: sd.numberOfGuests,
  unavailable: sd.unAvailable,
});

export function useSpaDaysList({
  propertyId,
  language,
  categoryFilterId,
  categoryFilterValue,
  date,
  guests,
}: UseSpaDaysListParams) {
  const { t } = useTranslations();
  const queryClient = useQueryClient();
  const prevParamsRef = useRef({ date, categoryFilterId, guests });

  useEffect(() => {
    const prev = prevParamsRef.current;
    if (
      prev.date !== date ||
      prev.categoryFilterId !== categoryFilterId ||
      prev.guests !== guests
    ) {
      queryClient.removeQueries({
        queryKey: getAvailableSpaPackagesKey(
          propertyId,
          { category: prev.categoryFilterId, date: prev.date, guests: prev.guests },
          language
        ),
      });
      prevParamsRef.current = { date, categoryFilterId, guests };
    }
  }, [date, categoryFilterId, guests, propertyId, language, queryClient]);

  const { data: spaDaysAll = [] } = useGetAllSpaPackages(propertyId, language);

  const { data: spaDays = [], isFetching } = useGetAvailableSpaPackages(
    propertyId,
    { category: categoryFilterId, date, guests },
    language
  );

  const { data: spaDaysPage } = useGetSpaDaysPage(propertyId, language);
  const fallbackImageUrl = spaDaysPage?.heroBackgroundImage?.url;

  const { isInitialLoading, isRefetching } = useLoadingState(isFetching, spaDays.length > 0);

  const sortedAvailable = (spaDays || []).slice().sort((a, b) => a.displayOrder - b.displayOrder);
  const notAvailableEntities = filterNotAvailableEntities(spaDaysAll, sortedAvailable);
  const allEntities = [...sortedAvailable, ...notAvailableEntities];
  const categoryFiltered =
    categoryFilterValue === UNCATEGORIZED_VALUE
      ? allEntities.filter(({ categories }) => !categories || categories.length === 0)
      : categoryFilterId
      ? allEntities.filter(({ categories }) =>
          categories.find((category) => String(category.id) === String(categoryFilterId))
        )
      : allEntities;

  const filteredEntities = guests
    ? categoryFiltered.filter((sd) => !sd.numberOfGuests || guests % sd.numberOfGuests === 0)
    : categoryFiltered;

  const listItems: SpaListItem[] = filteredEntities.map((sd) =>
    mapToListItem(sd, t, fallbackImageUrl)
  );

  const allListItems: SpaListItem[] = allEntities.map((sd) =>
    mapToListItem(sd, t, fallbackImageUrl)
  );

  return {
    listItems,
    allListItems,
    isFetching,
    isInitialLoading,
    isRefetching,
  };
}
